import { colors } from '../constants/colors';
import { Link } from 'react-router-dom';

export function PrivacyPolicyPage() {
  return (
    <div style={{ flex: 1, padding: '40px 24px', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '28px', fontWeight: '700', color: colors.ink.black, marginBottom: '8px' }}>Privacy Policy</h1>
      <p style={{ color: colors.gray[600], fontSize: '14px', marginBottom: '32px' }}>Last updated: April 16, 2026</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', color: colors.gray[900], fontSize: '15px', lineHeight: '1.7' }}>
        <section>
          <h2 style={sectionTitle}>1. Introduction</h2>
          <p>
            Snagg, LLC ("Snagg," "we," "us," or "our") operates the Snagg platform, including the website at snagg.live
            and the Snagg mobile application (collectively, the "Service"). This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you use our Service.
          </p>
          <p style={{ marginTop: '8px' }}>
            By using the Service, you agree to the collection and use of information in accordance with this policy.
            If you do not agree, please do not use the Service.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>2. Information We Collect</h2>

          <h3 style={subTitle}>Information You Provide</h3>
          <ul style={listStyle}>
            <li><strong>Account information:</strong> name, email address, phone number, password, and profile photo when you create an account.</li>
            <li><strong>Social login data:</strong> if you sign in with Google, Facebook, or Apple, we receive your name, email, and profile picture from those services. We do not receive your password from those providers.</li>
            <li><strong>Payment information:</strong> payment card details and billing address, processed securely by our payment processor Stripe. We do not store full card numbers on our servers.</li>
            <li><strong>Shipping information:</strong> shipping addresses you provide for purchases.</li>
            <li><strong>Seller information:</strong> if you sell on Snagg, we collect information needed to verify your identity and facilitate payouts, including bank account details (processed by Stripe Connect).</li>
            <li><strong>Messages:</strong> content of messages you send through the Snagg messaging system.</li>
            <li><strong>Show and listing content:</strong> photos, descriptions, and other content you upload for items and live shows.</li>
          </ul>

          <h3 style={subTitle}>Information Collected Automatically</h3>
          <ul style={listStyle}>
            <li><strong>Device information:</strong> device type, operating system, browser type, and unique device identifiers.</li>
            <li><strong>Usage data:</strong> pages visited, features used, shows watched, items viewed, and interaction timestamps.</li>
            <li><strong>Log data:</strong> IP address, access times, and referring URLs.</li>
            <li><strong>Live video data:</strong> when you participate in or watch live shows, we process video and audio streams in real time. We do not record or store live show video unless explicitly stated.</li>
          </ul>
        </section>

        <section>
          <h2 style={sectionTitle}>3. How We Use Your Information</h2>
          <ul style={listStyle}>
            <li>Provide, operate, and maintain the Service, including live auctions, buy-it-now sales, and order fulfillment.</li>
            <li>Process transactions, send purchase confirmations, and manage shipping.</li>
            <li>Verify your identity and prevent fraud.</li>
            <li>Facilitate seller payouts and settlement.</li>
            <li>Send you transactional communications (order updates, shipping notifications, password resets).</li>
            <li>Send promotional communications, which you can opt out of at any time.</li>
            <li>Improve and personalize the Service, including analyzing usage patterns.</li>
            <li>Monitor and analyze trends, usage, and activity.</li>
            <li>Detect, prevent, and address technical issues and security incidents.</li>
            <li>Comply with legal obligations.</li>
          </ul>
        </section>

        <section>
          <h2 style={sectionTitle}>4. SMS Messaging</h2>
          <p>
            When you create an account on Snagg, we collect your phone number and send you an SMS message containing a one-time verification code to confirm your identity.
            This is the only purpose for which we send SMS messages. We do not send marketing or promotional text messages.
          </p>
          <ul style={listStyle}>
            <li><strong>Message frequency:</strong> One SMS per verification attempt (account creation or password reset).</li>
            <li><strong>Message and data rates may apply</strong> depending on your mobile carrier and plan.</li>
            <li><strong>Opt out:</strong> Reply STOP to any message to stop receiving SMS from Snagg. Note that opting out may prevent you from completing account verification.</li>
            <li><strong>Help:</strong> Reply HELP to any message for assistance, or contact us at support@snagg.live.</li>
            <li><strong>Carriers:</strong> Carriers are not liable for delayed or undelivered messages.</li>
            <li><strong>No sharing with third parties:</strong> No mobile information, including phone numbers, collected as part of our SMS program will be shared with third parties or affiliates for marketing or promotional purposes. Phone numbers are shared only with our SMS delivery provider (Twilio) solely to deliver the verification message you requested.</li>
          </ul>
        </section>

        <section>
          <h2 style={sectionTitle}>5. How We Share Your Information</h2>
          <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
          <ul style={listStyle}>
            <li><strong>With other users:</strong> your public profile (display name, profile photo, username) is visible to other users. Sellers can see buyer shipping addresses for orders they fulfill.</li>
            <li><strong>Service providers:</strong> we share information with third-party vendors who perform services on our behalf, including Stripe (payments), 100ms (live video), and cloud hosting providers.</li>
            <li><strong>Legal requirements:</strong> we may disclose your information if required by law, regulation, legal process, or governmental request.</li>
            <li><strong>Safety and fraud prevention:</strong> we may share information to protect the rights, safety, and property of Snagg, our users, or the public.</li>
            <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
          </ul>
        </section>

        <section>
          <h2 style={sectionTitle}>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed to provide the Service.
            We may also retain certain information as required by law, to resolve disputes, enforce agreements, or for legitimate
            business purposes such as fraud prevention.
          </p>
          <p style={{ marginTop: '8px' }}>
            If you delete your account, we will delete or anonymize your personal information within 30 days, except where
            retention is required by law or for legitimate business purposes (e.g., transaction records for tax compliance).
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>7. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your information, including encryption of data in transit
            (TLS/SSL) and at rest, secure password hashing, and access controls. However, no method of transmission or storage
            is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>8. Your Rights and Choices</h2>
          <ul style={listStyle}>
            <li><strong>Access and update:</strong> you can access and update your account information through your account settings.</li>
            <li><strong>Delete your account:</strong> you can request account and data deletion by emailing privacy@snagg.live. See our <Link to="/data-deletion" style={{ color: colors.brand.blue, textDecoration: 'none', fontWeight: 600 }}>Data Deletion</Link> page for details. We will process your request within 30 days.</li>
            <li><strong>Marketing opt-out:</strong> you can unsubscribe from promotional emails using the link in each email.</li>
            <li><strong>Push notifications:</strong> you can manage push notification preferences through your device settings or the app.</li>
          </ul>
          <p style={{ marginTop: '8px' }}>
            If you are a resident of California, the EU/EEA, or another jurisdiction with applicable privacy laws, you may have
            additional rights including the right to access, correct, delete, or port your personal data, and the right to opt out
            of certain processing. To exercise these rights, contact us at the address below.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>9. Children's Privacy</h2>
          <p>
            The Service is not intended for children under the age of 18. We do not knowingly collect personal information from
            children under 18. If we learn that we have collected information from a child under 18, we will promptly delete it.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>10. Third-Party Services</h2>
          <p>
            The Service may contain links to or integrations with third-party websites and services (e.g., Stripe for payments,
            social login providers). We are not responsible for the privacy practices of these third parties. We encourage you to
            review their privacy policies.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated
            policy on this page and updating the "Last updated" date. Your continued use of the Service after changes are posted
            constitutes your acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>12. Contact Us</h2>
          <p>If you have questions about this Privacy Policy or our data practices, please contact us at:</p>
          <p style={{ marginTop: '8px' }}>
            Snagg, LLC<br />
            Email: support@snagg.live
          </p>
        </section>
      </div>
    </div>
  );
}

const sectionTitle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '700',
  color: '#000000',
  marginBottom: '8px',
};

const subTitle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: '600',
  color: '#000000',
  marginTop: '12px',
  marginBottom: '4px',
};

const listStyle: React.CSSProperties = {
  paddingLeft: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};
